import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { AuthService } from '@com.maessa.payroll.frontend/auth';
import { HasPermissionDirective, HasRoleDirective } from '@com.maessa.payroll.frontend/directives';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatListModule,
    MatIconModule,
    MatDividerModule,
    HasPermissionDirective,
    HasRoleDirective,
  ],
  template: `
    <div class="sidebar">
      <!-- Logo corporativo Maessa Naval -->
      <div class="sidebar-header">
        <div class="logo-wrapper" routerLink="/dashboard">
          <img src="/maessa-naval.png" alt="Maessa Naval" class="logo-img" />
        </div>
        <div class="brand-subtitle">Gestión de Horas</div>
      </div>

      <mat-divider class="sidebar-divider" />

      <!-- Navegación -->
      <mat-nav-list class="sidebar-nav">
        <a mat-list-item routerLink="/dashboard" routerLinkActive="active-nav-item" class="nav-item">
          <mat-icon matListItemIcon>dashboard</mat-icon>
          <span matListItemTitle>Dashboard</span>
        </a>

        <a
          *hasPermission="'worksheet.view'"
          mat-list-item
          routerLink="/work-sheets"
          routerLinkActive="active-nav-item"
          class="nav-item"
        >
          <mat-icon matListItemIcon>assignment</mat-icon>
          <span matListItemTitle>Partes de Trabajo</span>
        </a>

        <a
          *hasPermission="'reports.view'"
          mat-list-item
          routerLink="/reports"
          routerLinkActive="active-nav-item"
          class="nav-item"
        >
          <mat-icon matListItemIcon>bar_chart</mat-icon>
          <span matListItemTitle>Informes</span>
        </a>

        <a
          *hasPermission="'worksheet.view'"
          mat-list-item
          routerLink="/hours-database"
          routerLinkActive="active-nav-item"
          class="nav-item"
        >
          <mat-icon matListItemIcon>access_time</mat-icon>
          <span matListItemTitle>Base de Horas</span>
        </a>

        <mat-divider class="sidebar-divider" />

        <div class="section-label">Catálogos</div>

        <a
          *hasPermission="'employees.view'"
          mat-list-item
          routerLink="/employees"
          routerLinkActive="active-nav-item"
          class="nav-item"
        >
          <mat-icon matListItemIcon>people</mat-icon>
          <span matListItemTitle>Empleados</span>
        </a>

        <a
          *hasPermission="'oott.view'"
          mat-list-item
          routerLink="/oott"
          routerLinkActive="active-nav-item"
          class="nav-item"
        >
          <mat-icon matListItemIcon>work</mat-icon>
          <span matListItemTitle>OT / Proyectos</span>
        </a>

        <mat-divider class="sidebar-divider" />

        <a
          *hasRole="'administrator'"
          mat-list-item
          routerLink="/admin"
          routerLinkActive="active-nav-item"
          class="nav-item"
        >
          <mat-icon matListItemIcon>admin_panel_settings</mat-icon>
          <span matListItemTitle>Administración</span>
        </a>
      </mat-nav-list>

      <!-- Footer de la barra lateral -->
      <div class="sidebar-footer">
        <span class="version-label">v2.0 • Astilleros</span>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }

    .sidebar {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: #072142; /* Maessa Deep Corporate Navy */
      color: #ffffff;
      user-select: none;
    }

    .sidebar-header {
      padding: 16px 12px 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .logo-wrapper {
      background: #ffffff;
      border-radius: 8px;
      padding: 8px 14px;
      width: 100%;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    .logo-wrapper:hover {
      transform: scale(1.02);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
    }

    .logo-img {
      max-width: 100%;
      height: 38px;
      object-fit: contain;
    }

    .brand-subtitle {
      margin-top: 8px;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #8ec5ea; /* Cyan soft Maessa */
    }

    .sidebar-divider {
      border-color: rgba(255, 255, 255, 0.12) !important;
      margin: 8px 12px !important;
    }

    .section-label {
      padding: 6px 20px 2px;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: rgba(142, 197, 234, 0.7);
    }

    .sidebar-nav {
      flex: 1 1 auto;
      padding: 4px 6px;
      overflow-y: auto;
    }

    /* Elementos del menú con contraste máximo y variables M3 forzadas */
    .nav-item {
      margin: 4px 6px !important;
      border-radius: 8px !important;
      transition: background-color 0.18s ease, color 0.18s ease !important;
      --mdc-list-list-item-label-text-color: #f1f5f9 !important;
      --mdc-list-list-item-supporting-text-color: rgba(255, 255, 255, 0.7) !important;
      --mdc-list-list-item-leading-icon-color: #72b9ec !important;
    }

    .nav-item ::ng-deep .mat-mdc-list-item-title {
      color: #f8fafc !important;
      font-size: 0.88rem !important;
      font-weight: 500 !important;
      letter-spacing: 0.01em !important;
    }

    .nav-item ::ng-deep .mat-icon {
      color: #72b9ec !important;
      transition: transform 0.15s ease, color 0.15s ease;
    }

    .nav-item:hover {
      background: rgba(25, 131, 198, 0.22) !important;
    }

    .nav-item:hover ::ng-deep .mat-mdc-list-item-title {
      color: #ffffff !important;
    }

    .nav-item:hover ::ng-deep .mat-icon {
      color: #ffffff !important;
      transform: scale(1.1);
    }

    /* Elemento activo seleccionado */
    .nav-item.active-nav-item {
      background: linear-gradient(90deg, #1983c6 0%, #0d4482 100%) !important;
      box-shadow: 0 4px 12px rgba(25, 131, 198, 0.45) !important;
    }

    .nav-item.active-nav-item ::ng-deep .mat-mdc-list-item-title {
      color: #ffffff !important;
      font-weight: 700 !important;
    }

    .nav-item.active-nav-item ::ng-deep .mat-icon {
      color: #ffffff !important;
    }

    .sidebar-footer {
      padding: 12px 16px;
      text-align: center;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    .version-label {
      font-size: 0.7rem;
      color: rgba(255, 255, 255, 0.4);
      letter-spacing: 0.05em;
    }
  `],
})
export class SidebarComponent {
  readonly authService = inject(AuthService);
}
